/* Sinneszauber – Live-Daten aus Shopify (15.10.2026)
   Im Shopify-Theme (window.SZ.live === true) holt die Seite beim Laden Produkte, Preise, Bestand
   und Kategorien über die öffentliche Shop-API (/products.json, /collections/<handle>/products.json)
   und gleicht data.json damit ab:
   - Preise, Varianten, Titel und Lieferbarkeit kommen aus Shopify
   - neue Produkte erscheinen automatisch (Kategorien, Neuheiten, bei Düften auch im Duft-Mixer)
   - nicht mehr veröffentlichte Produkte verschwinden
   - der Bestand (bestand.js) wird aus Shopify gesetzt statt aus bestand.json
   Antwortet Shopify nicht (oder dauert es zu lange), bleibt alles wie in data.json.
   In der lokalen Vorschau ist SZ.live nicht gesetzt, dort passiert nichts. */
(function () {
  const TIMEOUT = 6000;
  const CACHE_KEY = 'sz-live-v1';
  const CACHE_MS = 5 * 60 * 1000;
  // Duft-Mixer: Kategorie -> Gruppe (wie in data.json)
  const MIXER_COLS = [['damendufte', 'Damen'], ['dufte', 'Herren'], ['luxusline-unisex', 'Unisex & Luxury']];

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const text = (html) => {
    // Zeilenumbrüche erhalten, sonst erkennt der Duft-Mixer die Duftpyramide nicht
    html = String(html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li|h[1-6])>/gi, '\n');
    try { return (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim(); }
    catch (_) { return String(html || '').replace(/<[^>]+>/g, ' ').trim(); }
  };

  async function pages(url) {
    const all = [];
    for (let page = 1; page <= 10; page++) {
      const r = await fetch(`${url}${url.includes('?') ? '&' : '?'}limit=250&page=${page}`, { credentials: 'same-origin' });
      if (!r.ok) throw new Error(url + ' ' + r.status);
      const { products } = await r.json();
      all.push(...products);
      if (products.length < 250) break;
    }
    return all;
  }

  // Nur die Felder behalten, die die Seite braucht (klein genug für den Sitzungsspeicher)
  const slim = (p) => ({
    handle: p.handle,
    title: p.title,
    body: p.body_html || '',
    created: p.published_at || p.created_at || '',
    image: p.images && p.images[0] ? p.images[0].src : '',
    variants: (p.variants || []).map((v) => ({ id: v.id, title: v.title, price: Number(v.price), available: !!v.available })),
  });

  async function load(colHandles) {
    try {
      const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (c && Date.now() - c.t < CACHE_MS) return c.d;
    } catch (_) {}
    const [products, ...cols] = await Promise.all([
      pages(`${root()}products.json`),
      ...colHandles.map((h) => pages(`${root()}collections/${encodeURIComponent(h)}/products.json`).then((l) => l.map((p) => p.handle)).catch(() => null)),
    ]);
    const d = { products: products.map(slim), cols: Object.fromEntries(colHandles.map((h, i) => [h, cols[i]])) };
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d })); } catch (_) {}
    return d;
  }

  function sync(p, lp) {
    const old = new Map((p.variants || []).map((v) => [String(v.id), v]));
    // Zusatzfelder (z. B. Variantenbild bei Make-up) bleiben erhalten
    p.variants = lp.variants.map((v) => Object.assign({}, old.get(String(v.id)) || {}, v));
    p.title = lp.title;
    p.available = p.variants.some((v) => v.available);
    const prices = p.variants.map((v) => v.price);
    p.priceMin = Math.min(...prices);
    p.priceMax = Math.max(...prices);
    if (!p.variants.some((v) => String(v.id) === String(p.variantId))) p.variantId = (p.variants.find((v) => v.available) || p.variants[0]).id;
    if ('multi' in p) p.multi = p.variants.length > 1;
    if (!p.image && lp.image) p.image = lp.image;
    return p;
  }

  function fresh(lp) {
    const notes = text(lp.body);
    return sync({
      handle: lp.handle,
      title: lp.title,
      url: `${root()}products/${lp.handle}`,
      image: lp.image ? lp.image + (lp.image.includes('?') ? '&' : '?') + 'width=800' : '',
      notes,
      desc: notes,
      variants: [],
      variantId: null,
      created: lp.created,
      groups: [],
      isLive: true,
    }, lp);
  }

  function merge(d, live) {
    const L = new Map(live.products.map((p) => [p.handle, p]));
    // Sicherheitsnetz: liefert Shopify deutlich weniger Produkte als erwartet, nichts entfernen
    const canRemove = live.products.length >= d.products.length * 0.6;
    const keep = (p) => p && (L.has(p.handle) || !canRemove || /^testerkit-luxury-unisex$/.test(p.handle));
    const upd = (p) => (L.has(p.handle) ? sync(p, L.get(p.handle)) : p);

    const known = new Set(d.products.map((p) => p.handle));
    const added = live.products.filter((lp) => !known.has(lp.handle)).map(fresh);

    d.products = d.products.filter(keep).map(upd).concat(added);
    const byHandle = new Map(d.products.map((p) => [p.handle, p]));

    // Neuheiten: neue Produkte vorne
    d.neu = added.slice().sort((a, b) => String(b.created).localeCompare(String(a.created)))
      .concat((d.neu || []).filter(keep).map(upd));

    // Kategorien: Zuordnung aus Shopify
    (d.collections || []).forEach((c) => {
      const hs = live.cols[c.handle];
      if (Array.isArray(hs)) c.products = hs.filter((h) => byHandle.has(h));
    });

    // Duft-Mixer: bekannte Düfte aktualisieren, neue Düfte aus den Duft-Kategorien ergänzen
    const mixer = (d.mixer || []).filter(keep).map(upd);
    const inMixer = new Map(mixer.map((p) => [p.handle, p]));
    MIXER_COLS.forEach(([col, group]) => {
      (live.cols[col] || []).forEach((h) => {
        if (inMixer.has(h)) return;
        const p = added.find((x) => x.handle === h);
        if (!p) return;
        const entry = Object.assign({}, p, { groups: [] });
        inMixer.set(h, entry);
        mixer.push(entry);
      });
      (live.cols[col] || []).forEach((h) => {
        const e = inMixer.get(h);
        if (e && e.isLive && !e.groups.includes(group)) e.groups.push(group);
      });
    });
    d.mixer = mixer;

    // Bestand aus Shopify
    const stock = {};
    d.products.forEach((p) => p.variants.forEach((v) => { stock[String(v.id)] = v.available ? 1 : 0; }));
    window.SZBestand && SZBestand.setLive && SZBestand.setLive(stock);

    d.live = { at: new Date().toISOString(), added: added.length, products: d.products.length };
    return d;
  }

  function apply(d) {
    if (!(window.SZ && SZ.live)) return Promise.resolve(d);
    const colHandles = (d.collections || []).map((c) => c.handle);
    let late = false;
    // kommt Shopify erst nach dem Timeout, wird nichts mehr nachträglich verändert
    const work = load(colHandles).then((live) => (late ? null : merge(d, live)));
    const timeout = new Promise((res) => setTimeout(() => { late = true; res(null); }, TIMEOUT));
    return Promise.race([work, timeout])
      .then((r) => r || d)
      .catch((e) => { try { console.warn('[Sinneszauber] Live-Daten nicht verfügbar, nutze data.json', e); } catch (_) {} return d; });
  }

  window.SZLive = { apply };
})();
